import React, { useRef, useMemo, useCallback, useEffect } from "react";
import type { Component, StageElement, ViewerDeviceRuntime } from "../../../types";
import { isNativeMobileDeviceType } from "../../../constants";
import { parseSpaPluginConfig, parseWidgetConfig } from "./parseSpaPluginConfig";
import type { SingleSpaSchema } from "./types";
import { SpaPluginActivityChrome } from "./components/SpaPluginActivityChrome";
import { SpaParcelBlock } from "./components/SpaParcelBlock";
import { SpaErrorBoundary } from "./layout/SpaErrorBoundary";

const STAGE_ELEMENT_SELECTOR = ".spf-rce-element";

type SingleSpaPluginComponentProps = {
  element: StageElement;
  componentConfig: Component;
  getAuthToken?: (callback: (token: string) => void) => void;
  viewerDevice?: ViewerDeviceRuntime;
};

function safeDomId(element: StageElement): string {
  return String(element.localRef || element.ref || "spa-plugin").replace(/[^a-zA-Z0-9_-]/g, "-");
}

function hasRichTextSchema(cfg: ReturnType<typeof parseSpaPluginConfig>): boolean {
  return Boolean(
    cfg.activityLabel || cfg.activityTitle || cfg.instructions || cfg.captions || cfg.credit,
  );
}

export const SingleSpaPluginComponent: React.FC<SingleSpaPluginComponentProps> = ({
  element,
  componentConfig,
  viewerDevice,
}) => {
  const cfg = parseSpaPluginConfig(componentConfig.config);
  const widgetConfig = useMemo(() => parseWidgetConfig(cfg.widgetConfig), [cfg.widgetConfig]);
  const domId = useMemo(() => safeDomId(element), [element]);
  const targetId = `spa-plugin-target-item-${domId}`;
  const componentId = componentConfig?.$id || "";

  const device = viewerDevice ?? { deviceType: "web" as const, isOnline: () => true };

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const resizeParentContainer = useCallback(() => {
    const safeHeight = 20;
    const stageEl = loaderRef.current?.closest(STAGE_ELEMENT_SELECTOR);
    if (stageEl && loaderRef.current) {
      (stageEl as HTMLElement).style.height = `${loaderRef.current.offsetHeight + safeHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (isNativeMobileDeviceType(device.deviceType) && !device.isOnline()) {
      const p = loaderRef.current?.parentElement;
      if (p) {
        p.setAttribute("style", "height:100% !important");
      }
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const el = loaderRef.current;
    if (!el) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        resizeParentContainer();
        timeoutId = null;
      }, 100);
    });
    resizeObserver.observe(el);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      resizeObserver.disconnect();
    };
  }, [device, resizeParentContainer]);

  const showChrome = hasRichTextSchema(cfg);
  const schema: SingleSpaSchema = useMemo(
    () =>
      showChrome
        ? {
            activityLabel: cfg.activityLabel,
            activityTitle: cfg.activityTitle,
            instructions: cfg.instructions,
            captions: cfg.captions,
            credit: cfg.credit,
          }
        : null,
    [showChrome, cfg],
  );

  const parcel = <SpaParcelBlock widgetConfig={widgetConfig} device={device} />;

  return (
    <SpaErrorBoundary name="BrontePluginSPAError">
      <div id={targetId} ref={loaderRef} className="bronte-single-spa-plugin" data-id={componentId}>
        {schema ? (
          <SpaPluginActivityChrome
            schema={schema}
            componentId={componentId}
            spaPluginTargetItemElId={targetId}
          >
            {parcel}
          </SpaPluginActivityChrome>
        ) : (
          parcel
        )}
      </div>
    </SpaErrorBoundary>
  );
};
